"use client"

import { Button } from "@/components/ui/button"
import { Phone, MapPin, Play, X } from "lucide-react"
import { useTranslations } from "next-intl";
import { siteConfig } from "@/config/site";
import { useState, useRef, useEffect } from "react";
import { getBlobUrl } from "@/lib/blob";
import { Sheet, SheetClose, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";

export function Hero() {
  const config = siteConfig;
	const t = useTranslations('page.homepage');
	const meta = useTranslations('meta');
  const tCarousel = useTranslations("common.carousel");
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const backgroundVideoRef = useRef<HTMLVideoElement>(null);
  const modalVideoRef = useRef<HTMLVideoElement>(null);

  const videoUrl:string = getBlobUrl('videos/Uzunov New New 4k-YGKdmGYWXY2Mh8p9xT4Mp0bIMR7461.mp4');

  const phone = config.contactInfo.phones[0];

  useEffect(() => {
    if (isVideoModalOpen) {
      backgroundVideoRef.current?.pause();
      return;
    }

    modalVideoRef.current?.pause();
    const playPromise = backgroundVideoRef.current?.play();
    if (playPromise && typeof playPromise.catch === "function") {
      playPromise.catch(() => {});
    }
  }, [isVideoModalOpen]);
  
  return (
    <>
      <section className="relative overflow-hidden py-12 sm:py-16 md:py-20 lg:py-32 min-h-[500px] sm:min-h-[600px] lg:min-h-[700px]">
        {/* Video Background. Scaled biggers as it has black bars on the sides */}
        <video
          ref={backgroundVideoRef}
          className="absolute inset-0 w-full h-full object-cover object-center z-0"
          style={{
            minWidth: '100%',
            minHeight: '100%',
            width: 'auto',
            height: '100%',
            transform: 'scale(1.15)'
          }}
          autoPlay
          muted
          loop
          playsInline
        >
            <source src={videoUrl} type="video/mp4" />
          </video>
        
        {/* Overlay for better text readability */}
        <div className="absolute inset-0 bg-black/60 z-10" />
        
        <div className="container px-4 sm:px-6 relative z-20">
          <div className="flex items-center justify-center min-h-[400px] sm:min-h-[500px] lg:min-h-[600px]">
            <div className="flex flex-col justify-center items-center space-y-4 sm:space-y-6 text-center max-w-4xl w-full">
              <div className="space-y-4 sm:space-y-6">
                <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl/none font-bold tracking-tighter text-white drop-shadow-lg px-2">
                  {t('hero.title')}
                  <span className="block text-teal-400 drop-shadow-lg"> {t('hero.highlight')}</span>
                </h1>
                <p className="max-w-[700px] mx-auto px-4 text-white/90 text-base sm:text-lg md:text-xl drop-shadow-md">
                  {t('hero.description')}
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                <Button size="lg" className="bg-teal-600 hover:bg-teal-700 text-white border-0 shadow-lg w-full sm:w-auto"
                  asChild>
                  <a href={`tel:${phone}`}>
                    <Phone className="me-2 h-4 w-4" />
                    {t('hero.button.book')}
                  </a>
                </Button>
                <Button 
                  size="lg" 
                  variant="outline" 
                  className="bg-white/10 hover:bg-white/20 text-white border-white/30 hover:border-white/50 backdrop-blur-sm shadow-lg w-full sm:w-auto"
                  onClick={() => setIsVideoModalOpen(true)}
                >
                  <Play className="me-2 h-4 w-4" />
                  {t('hero.watch_video')}
                </Button>
              </div>
              <div className="flex items-center gap-2 text-white/80 drop-shadow-md px-4 sm:px-0">
                <MapPin className="h-4 w-4 text-teal-400 flex-shrink-0" />
                <a 
                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(meta('address_street'))}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-teal-400 hover:underline transition-colors text-sm sm:text-base break-words"
                >
                  {meta('address_street')}
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Sheet open={isVideoModalOpen} onOpenChange={setIsVideoModalOpen}>
        <SheetContent
          side="top"
          aria-modal="true"
          overlayClassName="bg-black/95 backdrop-blur-none"
          className="inset-0 h-screen w-screen gap-0 border-none bg-transparent p-0 pt-0 shadow-none data-[state=closed]:slide-out-to-top-0 data-[state=open]:slide-in-from-top-0 data-[state=closed]:hidden data-[state=closed]:pointer-events-none"
        >
          <SheetHeader className="sr-only">
            <SheetTitle>{t("hero.watch_video")}</SheetTitle>
            <SheetDescription>{t("hero.description")}</SheetDescription>
          </SheetHeader>
          <div className="relative flex h-full w-full items-center justify-center">
            <SheetClose asChild>
              <Button
                variant="ghost"
                size="icon"
                className="absolute end-4 top-4 z-10 text-white hover:bg-white/10 hover:text-gray-200 focus-visible:ring-white/60 focus-visible:ring-offset-0"
                aria-label={tCarousel("close_video")}
              >
                <X className="h-6 w-6 sm:h-8 sm:w-8" />
              </Button>
            </SheetClose>
            <div className="flex max-h-[90vh] max-w-[90vw] items-center justify-center bg-black">
              <video
                ref={modalVideoRef}
                className="block h-auto max-h-[90vh] w-auto max-w-[90vw] bg-black"
                controls
                autoPlay
              >
                <source src={videoUrl} type="video/mp4" />
              </video>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </>
  )
}
